'use client'

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { User } from '@/types'
import { PropertyWithLandlord, PropertyFilter } from '@/types/admin'
import { useCurrency } from '@/lib/currency'
import { getApiUrl } from '@/lib/apiUrl'

interface PropertyManagementProps {
  properties: PropertyWithLandlord[]
  onUpdate: () => void
}

export default function PropertyManagement({ properties, onUpdate }: PropertyManagementProps) {
  const [loading, setLoading] = useState<string | null>(null)
  const [message, setMessage] = useState('')
  const [filter, setFilter] = useState<PropertyFilter['verification']>('all')
  const [availabilityFilter, setAvailabilityFilter] = useState<PropertyFilter['availability']>('all')
  const [selectedProperty, setSelectedProperty] = useState<PropertyWithLandlord | null>(null)
  const [showImageModal, setShowImageModal] = useState(false)
  const [modalTab, setModalTab] = useState<'images' | 'videos' | 'documents'>('images')
  const navigate = useNavigate()
  const { format } = useCurrency()

  // Filter properties based on selected filters
  const filteredProperties = properties.filter(property => {
    // Verification filter
    if (filter !== 'all') {
      const isVerified = property.is_verified === true
      if (filter === 'verified' && !isVerified) return false
      if (filter === 'pending' && isVerified) return false
      if (filter === 'rejected' && isVerified) return false
    }
    
    // Availability filter
    if (availabilityFilter !== 'all') {
      if (availabilityFilter === 'available' && !property.is_available) return false
      if (availabilityFilter === 'unavailable' && property.is_available) return false
    }
    
    return true
  })

  // Handle property availability toggle
  const handleToggleAvailability = async (propertyId: string, isAvailable: boolean) => {
    setLoading(propertyId)
    setMessage('')

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/admin/properties/${propertyId}/availability`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ isAvailable })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to update property availability')
      }

      await response.json() // Response data not used
      setMessage(`Property marked as ${isAvailable ? 'available' : 'unavailable'} successfully!`)
      onUpdate()
    } catch (error) {
      console.error('Availability toggle error:', error)
      setMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setLoading(null)
    }
  }

  // Handle property verification
  const handleVerifyProperty = async (propertyId: string, verified: boolean) => {
    setLoading(propertyId)
    setMessage('')

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/admin/properties/${propertyId}/verify`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': token ? `Bearer ${token}` : ''
        },
        body: JSON.stringify({ isVerified: verified })
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to update property verification')
      }

      await response.json() // Response data not used
      setMessage(`Property ${verified ? 'verified' : 'unverified'} successfully!`)
      // Refresh the data to reflect changes
      onUpdate()
    } catch (error) {
      console.error('Verification error:', error)
      setMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setLoading(null)
    }
  }

  // Handle property deletion
  const handleDeleteProperty = async (propertyId: string) => {
    if (!confirm('Are you sure you want to delete this property? This action cannot be undone.')) {
      return
    }

    setLoading(propertyId)
    setMessage('')

    try {
      const apiUrl = getApiUrl()
      const token = localStorage.getItem('authToken')
      const response = await fetch(`${apiUrl}/properties/${propertyId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': token ? `Bearer ${token}` : ''
        }
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.message || 'Failed to delete property')
      }

      setMessage('Property deleted successfully!')
      onUpdate()
    } catch (error) {
      console.error('Delete error:', error)
      setMessage(error instanceof Error ? error.message : 'An error occurred. Please try again.')
    } finally {
      setLoading(null)
    }
  }

  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Format duration
  const formatDuration = (months: number | null | undefined, rentalType?: string) => {
    if (!months) return rentalType === 'short-term' ? 'Short-term rental' : 'Not specified'
    if (months === 12) return '1 year'
    if (months === 6) return '6 months'
    if (months === 18) return '18 months'
    if (months === 24) return '2 years'
    return `${months} months`
  }
  
  // Get location display
  const getLocationDisplay = (property: PropertyWithLandlord) => {
    // If location is a string, use it directly
    if (typeof property.location === 'string' && property.location.trim()) {
      return property.location
    }
    
    // If address is an object, format it
    if (property.address && typeof property.address === 'object') {
      const addr = property.address as { street?: string; city?: string; state?: string; zipCode?: string; country?: string }
      const parts = []
      if (addr.street) parts.push(addr.street)
      if (addr.city) parts.push(addr.city)
      if (addr.state) parts.push(addr.state)
      if (parts.length > 0) {
        return parts.join(', ')
      }
    }
    
    // Fallback to city, state if available
    if ((property as any).city || (property as any).state) {
      return [(property as any).city, (property as any).state].filter(Boolean).join(', ') || 'Location not specified'
    }
    
    return 'Location not specified'
  }
  
  // Format date safely
  const formatDate = (date: string | Date | undefined) => {
    if (!date) return 'N/A'
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      if (isNaN(dateObj.getTime())) return 'N/A'
      return dateObj.toLocaleDateString()
    } catch {
      return 'N/A'
    }
  }

  // Get landlord display name - handle null/undefined landlords
  const getLandlordDisplayName = (landlord: User | null | undefined) => {
    // Handle null or undefined landlord
    if (!landlord) {
      return 'Unknown Landlord'
    }
    
    // Check if kyc_data exists and has firstName/lastName
    if (landlord.kyc_data?.firstName && landlord.kyc_data?.lastName) {
      return `${landlord.kyc_data.firstName} ${landlord.kyc_data.lastName}`
    }
    
    // Fall back to email or default
    return landlord.email || 'Unknown Landlord'
  }

  // Get verification status badge
  const getVerificationBadge = (property: PropertyWithLandlord) => {
    const isVerified = property.is_verified === true
    if (isVerified) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          ✅ Verified
        </span>
      )
    } else {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
          ⏳ Pending Review
        </span>
      )
    }
  }

  return (
    <div className="space-y-6 w-full overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h2 className="text-xl font-semibold text-gray-900">Property Management</h2>
        <div className="text-sm text-gray-500">
          Total: {properties.length} properties
        </div>
      </div>

      {/* Verification Alert */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Property Verification Required</h3>
            <div className="mt-2 text-sm text-blue-700">
              <p>All properties must be verified by admin before they become visible to clients. Review property details and images to ensure authenticity.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid gap-4 sm:grid-cols-2 md:grid-cols-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Verification Status</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as 'all' | 'verified' | 'pending' | 'rejected')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Properties</option>
              <option value="verified">✅ Verified</option>
              <option value="pending">⏳ Pending Review</option>
              <option value="rejected">❌ Rejected</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Availability Status</label>
            <select
              value={availabilityFilter}
              onChange={(e) => setAvailabilityFilter(e.target.value as 'all' | 'available' | 'unavailable')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="all">All Properties</option>
              <option value="available">✅ Available</option>
              <option value="unavailable">❌ Unavailable</option>
            </select>
          </div>
          <div className="hidden md:block">
            <label className="block text-sm font-medium text-gray-700 mb-1">Quick insight</label>
            <div className="px-3 py-2 rounded-md bg-emerald-50 text-emerald-700 text-sm">
              {filteredProperties.length} visible • {properties.filter(p => p.is_verified !== true).length} pending
            </div>
          </div>
        </div>
      </div>

      {/* Message Display */}
      {message && (
        <div className={`p-3 rounded-md text-sm ${
          message.includes('error') || message.includes('Failed') 
            ? 'bg-red-100 text-red-700' 
            : 'bg-green-100 text-green-700'
        }`}>
          {message}
        </div>
      )}

      {/* Properties List */}
      <div className="bg-white rounded-lg shadow">
        {filteredProperties.length > 0 ? (
          <>
            {/* Card view for small screens */}
            <div className="grid sm:hidden gap-4 p-4">
              {filteredProperties.map((property) => {
                const docCount = (() => {
                  const docs = property.house_documents || (property as any).houseDocuments || []
                  return Array.isArray(docs) ? docs.length : 0
                })()
                return (
                  <div key={property.id} className="border border-gray-100 rounded-xl p-4 bg-white shadow-sm">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-sm text-gray-500 mb-1">#{property.id?.slice(-6)}</p>
                        <h3 className="text-lg font-semibold text-gray-900 leading-tight">{property.title}</h3>
                        <p className="text-sm text-gray-500">{getLocationDisplay(property)}</p>
                        <p className="text-xs text-gray-400 mt-1 line-clamp-2">{property.description}</p>
                      </div>
                      <div className="text-right space-y-1">
                        {getVerificationBadge(property)}
                        <span className={`block text-xs px-2 py-1 rounded-full ${property.is_available ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                          {property.is_available ? 'Available' : 'Unavailable'}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 grid grid-cols-2 gap-3 text-sm text-gray-700">
                      <div>
                        <p className="text-xs text-gray-500">Price</p>
                        <p className="font-semibold text-emerald-700">{formatPrice(property.price)}</p>
                        <p className="text-xs text-gray-500">{property.rentalType === 'short-term' ? '/day' : '/month'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Duration</p>
                        <p className="font-semibold">{formatDuration(property.duration || (property as any).leaseTerms?.minLease, property.rentalType)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Media</p>
                        <p className="font-semibold">{property.images?.length || 0} images • {property.videos?.length || 0} videos</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Documents</p>
                        <p className="font-semibold">{docCount} docs</p>
                      </div>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      <button
                        onClick={() => navigate(`/admin/properties/${property.id}`)}
                        className="px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      >
                        View Details
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProperty(property)
                          setShowImageModal(true)
                          setModalTab('images')
                        }}
                        className="px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100"
                      >
                        Media
                      </button>
                      <button
                        onClick={() => handleToggleAvailability(property.id, !property.is_available)}
                        disabled={loading === property.id}
                        className="px-3 py-2 rounded-lg bg-stone-100 text-stone-700 hover:bg-stone-200 disabled:opacity-50"
                      >
                        {loading === property.id ? 'Updating...' : property.is_available ? 'Mark Unavailable' : 'Mark Available'}
                      </button>
                      {property.is_verified !== true ? (
                        <button
                          onClick={() => handleVerifyProperty(property.id, true)}
                          disabled={loading === property.id}
                          className="px-3 py-2 rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50"
                        >
                          {loading === property.id ? 'Verifying...' : 'Verify'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleVerifyProperty(property.id, false)}
                          disabled={loading === property.id}
                          className="px-3 py-2 rounded-lg bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50"
                        >
                          {loading === property.id ? 'Updating...' : 'Unverify'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteProperty(property.id)}
                        disabled={loading === property.id}
                        className="px-3 py-2 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        {loading === property.id ? 'Deleting...' : 'Delete'}
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>

            {/* Table view for medium+ screens */}
            <div className="hidden sm:block">
              <div className="overflow-x-auto max-w-full">
                <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Property
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Landlord
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Duration
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Availability
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Images
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Videos
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Documents
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Listed
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredProperties.map((property) => (
                    <tr key={property.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {property.title}
                          </div>
                          <div className="text-sm text-gray-500">{getLocationDisplay(property)}</div>
                          <div className="text-xs text-gray-400 mt-1">{property.description.substring(0, 50)}...</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {getLandlordDisplayName(property.landlord)}
                          </div>
                          <div className="text-sm text-gray-500">
                            {property.landlord?.email || 'N/A'}
                          </div>
                          <div className="text-xs text-gray-400">
                            {property.landlord?.is_verified ? '✅ Verified Landlord' : '⏳ Pending Verification'}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {formatPrice(property.price)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {property.rentalType === 'short-term' ? '/day' : '/month'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDuration(property.duration || (property as any).leaseTerms?.minLease, property.rentalType)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            property.is_available 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {property.is_available ? '✅ Available' : '❌ Unavailable'}
                          </span>
                          <button
                            onClick={() => handleToggleAvailability(property.id, !property.is_available)}
                            disabled={loading === property.id}
                            className={`text-xs px-2 py-1 rounded ${
                              property.is_available
                                ? 'text-red-600 hover:text-red-700 bg-red-50 hover:bg-red-100'
                                : 'text-green-600 hover:text-green-700 bg-green-50 hover:bg-green-100'
                            } disabled:opacity-50`}
                          >
                            {loading === property.id ? 'Updating...' : 
                             property.is_available ? 'Mark Unavailable' : 'Mark Available'}
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getVerificationBadge(property)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.images?.length || 0} images
                        </div>
                        {property.images && property.images.length > 0 && (
                          <button
                            onClick={() => {
                              setSelectedProperty(property)
                              setShowImageModal(true)
                            }}
                            className="text-xs text-blue-600 hover:text-blue-700 mt-1"
                          >
                            View Images
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {property.videos?.length || 0} videos
                        </div>
                        {property.videos && property.videos.length > 0 && (
                          <button
                            onClick={() => {
                              setSelectedProperty(property)
                              setShowImageModal(true)
                            }}
                            className="text-xs text-purple-600 hover:text-purple-700 mt-1"
                          >
                            View Videos
                          </button>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {(() => {
                            const docs = property.house_documents || (property as any).houseDocuments || []
                            return Array.isArray(docs) ? docs.length : 0
                          })()} docs
                        </div>
                        {(() => {
                          const docs = property.house_documents || (property as any).houseDocuments || []
                          const docCount = Array.isArray(docs) ? docs.length : 0
                          if (docCount > 0) {
                            return (
                              <button
                                onClick={() => {
                                  setSelectedProperty(property)
                                  setModalTab('documents')
                                  setShowImageModal(true)
                                }}
                                className="text-xs text-green-600 hover:text-green-700 mt-1"
                              >
                                View Documents
                              </button>
                            )
                          }
                          return null
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(property.created_at || (property as any).createdAt)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex flex-col space-y-2">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => navigate(`/admin/properties/${property.id}`)}
                              className="text-blue-600 hover:text-blue-700 text-xs"
                            >
                              View Details
                            </button>
                            {property.images && property.images.length > 0 && (
                              <button
                                onClick={() => {
                                  setSelectedProperty(property)
                                  setShowImageModal(true)
                                }}
                                className="text-green-600 hover:text-green-700 text-xs"
                              >
                                Review Images
                              </button>
                            )}
                          </div>
                          <div className="flex space-x-2">
                            {property.is_verified !== true ? (
                              <button
                                onClick={() => handleVerifyProperty(property.id, true)}
                                disabled={loading === property.id}
                                className="text-green-600 hover:text-green-700 disabled:opacity-50 text-xs"
                              >
                                {loading === property.id ? 'Verifying...' : '✅ Verify'}
                              </button>
                            ) : (
                              <button
                                onClick={() => handleVerifyProperty(property.id, false)}
                                disabled={loading === property.id}
                                className="text-yellow-600 hover:text-yellow-700 disabled:opacity-50 text-xs"
                              >
                                {loading === property.id ? 'Updating...' : '⏳ Unverify'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDeleteProperty(property.id)}
                              disabled={loading === property.id}
                              className="text-red-600 hover:text-red-700 disabled:opacity-50 text-xs"
                            >
                              {loading === property.id ? 'Deleting...' : '🗑️ Delete'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </>
        ) : (
          <div className="p-6 text-center text-gray-500">
            <p>No properties found matching the current filters.</p>
          </div>
        )}
      </div>

      {/* Property Images Modal */}
      {showImageModal && selectedProperty && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Property Details - {selectedProperty.title}
                </h3>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              {/* Tabs */}
              <div className="flex space-x-1 mb-4 bg-gray-100 p-1 rounded-lg">
                <button
                  onClick={() => setModalTab('images')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    modalTab === 'images'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📸 Images ({selectedProperty.images?.length || 0})
                </button>
                <button
                  onClick={() => setModalTab('videos')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    modalTab === 'videos'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  🎥 Videos ({selectedProperty.videos?.length || 0})
                </button>
                <button
                  onClick={() => setModalTab('documents')}
                  className={`flex-1 py-2 px-4 text-sm font-medium rounded-md transition-colors ${
                    modalTab === 'documents'
                      ? 'bg-white text-gray-900 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  📄 Documents ({(() => {
                    const docs = selectedProperty.house_documents || (selectedProperty as any).houseDocuments || []
                    return Array.isArray(docs) ? docs.length : 0
                  })()})
                </button>
              </div>

              {/* Images Tab */}
              {modalTab === 'images' && (
                <>
                  <div className="mb-4 p-4 bg-blue-50 rounded-lg">
                    <h4 className="font-medium text-blue-900 mb-2">Image Review Guidelines:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Check if images are clear and show the actual property</li>
                      <li>• Verify the property exists and matches the description</li>
                      <li>• Look for signs of authenticity (real furniture, proper lighting)</li>
                      <li>• Ensure images are not stock photos or misleading</li>
                    </ul>
                  </div>

                  {selectedProperty.images && selectedProperty.images.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                      {selectedProperty.images.map((imageUrl: string, index: number) => (
                        <div key={index} className="relative">
                          <img
                            src={typeof imageUrl === 'string' ? imageUrl : (imageUrl as any).url || imageUrl}
                            alt={`Property image ${index + 1}`}
                            className="w-full h-48 object-cover rounded-lg border"
                            onError={(e) => {
                              e.currentTarget.src = '/placeholder-property.svg'
                            }}
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                            {index + 1}
                          </div>
                          {index === 0 && (
                            <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                              Primary
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No images uploaded for this property</p>
                    </div>
                  )}
                </>
              )}

              {/* Videos Tab */}
              {modalTab === 'videos' && (
                <>
                  <div className="mb-4 p-4 bg-purple-50 rounded-lg">
                    <h4 className="font-medium text-purple-900 mb-2">Video Review Guidelines:</h4>
                    <ul className="text-sm text-purple-800 space-y-1">
                      <li>• Check video quality and clarity</li>
                      <li>• Verify videos show the actual property</li>
                      <li>• Ensure content is appropriate and professional</li>
                      <li>• Look for comprehensive property coverage</li>
                    </ul>
                  </div>

                  {selectedProperty.videos && selectedProperty.videos.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                      {selectedProperty.videos.map((videoUrl, index) => (
                        <div key={index} className="relative">
                          <video
                            src={videoUrl}
                            className="w-full h-48 object-cover rounded-lg border"
                            controls
                            preload="metadata"
                          />
                          <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                            {index + 1}
                          </div>
                          {index === 0 && (
                            <div className="absolute top-2 left-2 bg-purple-500 text-white text-xs px-2 py-1 rounded">
                              Featured
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <p>No videos uploaded for this property</p>
                    </div>
                  )}
                </>
              )}

              {/* Documents Tab */}
              {modalTab === 'documents' && (
                <>
                  <div className="mb-4 p-4 bg-green-50 rounded-lg">
                    <h4 className="font-medium text-green-900 mb-2">Document Review Guidelines:</h4>
                    <ul className="text-sm text-green-800 space-y-1">
                      <li>• Verify document authenticity and completeness</li>
                      <li>• Check if documents match the property details</li>
                      <li>• Ensure all required documents are present</li>
                      <li>• Look for proper government stamps and signatures</li>
                    </ul>
                  </div>

                  {(() => {
                    // Get documents from either house_documents or houseDocuments
                    const docs = selectedProperty.house_documents || (selectedProperty as any).houseDocuments || []
                    const docUrls = Array.isArray(docs) ? docs.map((doc: any) => {
                      if (typeof doc === 'string') return doc
                      if (doc && typeof doc === 'object' && doc.url) return doc.url
                      return doc
                    }).filter((url: any) => url && typeof url === 'string') : []
                    
                    if (docUrls.length > 0) {
                      return (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
                          {docUrls.map((docUrl: string, index: number) => (
                            <div key={index} className="relative">
                              <div className="w-full h-48 bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                                <div className="text-center">
                                  <div className="text-4xl text-gray-400 mb-2">📄</div>
                                  <div className="text-sm text-gray-700">Document {index + 1}</div>
                                  <a
                                    href={docUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-block mt-2 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700"
                                  >
                                    View Document
                                  </a>
                                </div>
                              </div>
                              <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                                {index + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    }
                    return (
                      <div className="text-center py-8 text-gray-500">
                        <p>No house documents uploaded for this property</p>
                      </div>
                    )
                  })()}
                </>
              )}


              <div className="mt-6 flex justify-end space-x-3">
                <button
                  onClick={() => setShowImageModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Close
                </button>
                                 {selectedProperty.is_verified !== true && (
                   <button
                     onClick={() => {
                       handleVerifyProperty(selectedProperty.id, true)
                       setShowImageModal(false)
                     }}
                     disabled={loading === selectedProperty.id}
                     className="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 rounded-md"
                   >
                     {loading === selectedProperty.id ? 'Verifying...' : '✅ Verify Property'}
                   </button>
                 )}
                 
                 <div className="text-sm text-gray-600 mt-2">
                   <p><strong>Images:</strong> {selectedProperty.images?.length || 0} uploaded</p>
                   <p><strong>Videos:</strong> {selectedProperty.videos?.length || 0} uploaded</p>
                   <p><strong>Documents:</strong> {(() => {
                     const docs = selectedProperty.house_documents || (selectedProperty as any).houseDocuments || []
                     return Array.isArray(docs) ? docs.length : 0
                   })()} uploaded</p>
                 </div>
              </div>
            </div>
          </div>
        </div>
      )}
      

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Total Properties</h3>
          <p className="text-2xl font-bold text-gray-900">{properties.length}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Verified Properties</h3>
          <p className="text-2xl font-bold text-green-600">
            {properties.filter(p => p.is_verified === true).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Pending Review</h3>
          <p className="text-2xl font-bold text-yellow-600">
            {properties.filter(p => p.is_verified !== true).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Available Properties</h3>
          <p className="text-2xl font-bold text-green-600">
            {properties.filter(p => p.is_available).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Unavailable Properties</h3>
          <p className="text-2xl font-bold text-red-600">
            {properties.filter(p => !p.is_available).length}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="text-sm font-medium text-gray-500">Average Price</h3>
          <p className="text-2xl font-bold text-blue-600">
            {properties.length > 0 
              ? formatPrice(properties.reduce((sum, p) => sum + p.price, 0) / properties.length)
              : formatPrice(0)
            }
          </p>
        </div>
      </div>
    </div>
  )
} 



